<template>
  <div>
    <div class="columns">
      <div class="column is-full-mobile is-6 ml-auto">
        <queclink-tracker-search />
      </div>
    </div>
    <div class="tile is-ancestor">
      <div class="tile is-parent">
        <div class="tile is-child card">
          <div class="card-header">
            <h2 class="card-header-title">Informations</h2>
            <div class="my-auto mx-3 is-flex is-align-items-center">
              <b-tooltip
                :label="wears.find((wear) => wear.value === queclinkTracker.wear).label"
                type="is-light"
                position="is-bottom"
              >
                <b-icon
                  :icon="wearIcons[queclinkTracker.wear].icon"
                  :type="wearIcons[queclinkTracker.wear].type"
                />
              </b-tooltip>
              <b-tag :type="tags[queclinkTracker.availability].type" class="ml-2">
                {{ tags[queclinkTracker.availability].label }}
              </b-tag>
            </div>
          </div>
          <div class="card-content">
            <table class="table is-striped is-fullwidth">
              <tr>
                <th>Numéro</th>
                <td>{{ queclinkTracker.number }}</td>
              </tr>
              <tr>
                <th>IMEI</th>
                <td>{{ queclinkTracker.imei }}</td>
              </tr>
              <tr>
                <th>Date d'achat</th>
                <td>{{ formatDate(queclinkTracker.purchasedAt) }}</td>
              </tr>
              <tr>
                <th>Locations depuis l'achat</th>
                <td>{{ lives.length }}</td>
              </tr>
              <tr>
                <th>Location sur l'année glissante</th>
                <td>{{ totalLivesSinceLastYear }}</td>
              </tr>
              <tr>
                <th>Dernier point relevé</th>
                <td>
                  <span v-if="showLastKnownLocationInformations">{{
                    formatDateWithTime(lastKnownLocation?.locatedAt ?? '')
                  }}</span>
                  <span v-else>–</span>
                </td>
              </tr>
              <tr>
                <th>Dernier niveau de batterie</th>
                <td>
                  <o-battery-level-indicator
                    v-if="showLastKnownLocationInformations"
                    :battery-percentage="lastKnownLocation.batteryPercentage"
                  />
                  <span v-else>–</span>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
      <div class="tile is-parent">
        <div class="tile is-child card">
          <queclink-tracker-map :last-known-location="lastKnownLocation" class="card" />
        </div>
      </div>
    </div>
    <div class="tile is-ancestor">
      <div class="tile is-parent">
        <div class="tile is-child card">
          <div class="card-header">
            <h2 class="card-header-title">Changement d'état</h2>
          </div>
          <div class="card-content">
            <queclink-tracker-edit-form
              :queclink-tracker="queclinkTracker"
              @update:queclinkTracker="$emit('update:queclinkTracker', $event)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="tile is-ancestor">
      <div class="tile is-parent">
        <div class="tile is-child card">
          <div class="card-header">
            <h2 class="card-header-title">Lives</h2>
          </div>
          <div class="card-content">
            <queclink-tracker-live-table :lives="formattedLives" :loading="loading" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, isWithinInterval, subYears } from 'date-fns'
import QueclinkTrackerMap from './QueclinkTrackerMap.vue'
import QueclinkTrackerSearch from './QueclinkTrackerSearch.vue'
import QueclinkTrackerEditForm from './QueclinkTrackerEditForm.vue'
import QueclinkTrackerLiveTable from './QueclinkTrackerLiveTable.vue'
import { TAGS } from '@constants/tracker/tag'
import { WEARS, WEAR_ICONS } from '@constants/tracker/wear'
import OBatteryLevelIndicator from '@components/BatteryLevelIndicator.vue'

export default {
  name: 'QueclinkTrackerInformation',

  components: {
    OBatteryLevelIndicator,
    QueclinkTrackerMap,
    QueclinkTrackerSearch,
    QueclinkTrackerEditForm,
    QueclinkTrackerLiveTable,
  },

  props: {
    queclinkTracker: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      lives: [],
      liveVehicles: [],
      lastKnownLocation: null,
    }
  },

  created() {
    this.tags = TAGS
    this.wears = WEARS
    this.wearIcons = WEAR_ICONS
  },

  watch: {
    queclinkTracker: {
      handler() {
        this.load()
      },
      immediate: true,
    },
  },

  computed: {
    showLastKnownLocationInformations() {
      return this.lastKnownLocation !== null
    },

    totalLivesSinceLastYear() {
      return this.lives.filter(
        (live) =>
          isWithinInterval(live.startedAt, {
            start: subYears(new Date(), 1),
            end: new Date(),
          }) ||
          isWithinInterval(live.endedAt, {
            start: subYears(new Date(), 1),
            end: new Date(),
          }) ||
          isWithinInterval(new Date(), {
            start: live.startedAt,
            end: live.endedAt,
          }),
      ).length
    },

    formattedLives() {
      return this.lives.map((live) => ({
        ...live,
        vehicleId: this.liveVehicles[live.id]?.id,
        vehicleNumber: this.liveVehicles[live.id]?.number,
      }))
    },
  },

  methods: {
    async load() {
      this.loading = true

      await Promise.all([
        this.fetchAllLivesByQueclinkTracker(),
        this.fetchLiveVehiclesByQueclinkTracker(),
        this.fetchTrackerLastKnownLocation(),
      ])

      this.loading = false
    },

    async fetchAllLivesByQueclinkTracker() {
      try {
        this.lives = await this.$services.liveService.getAllByQueclinkTrackerId(
          this.queclinkTracker.id,
        )
      } catch {
        this.lives = []
      }
    },

    async fetchLiveVehiclesByQueclinkTracker() {
      try {
        this.liveVehicles = (
          await this.$services.liveVehicleService.getAllByQueclinkTrackerId(this.queclinkTracker.id)
        ).reduce(
          (vehicles, vehicle) => ({
            ...vehicles,
            [vehicle.liveId]: { ...vehicle },
          }),
          {},
        )
      } catch {
        this.liveVehicles = []
      }
    },

    async fetchTrackerLastKnownLocation() {
      try {
        this.lastKnownLocation = await this.$services.queclinkTrackerService.getLatestLocation(
          this.queclinkTracker.id,
        )
      } catch {
        this.lastKnownLocation = null
      }
    },

    formatDate(date) {
      return !date ? 'Aucune date' : format(date, 'dd/MM/yyyy')
    },

    formatDateWithTime(dateString) {
      return !dateString ? 'Aucune date' : format(new Date(dateString), 'dd/MM/yyyy à HH:mm')
    },
  },
}
</script>
